import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { LoaderData } from "../../../components/common/LoaderData";
import { useCustomSnackbar } from "../../../utils/hooks/useCustomSnackbar";
import AvrTable from "./components/AvrTable";
import AddAvrModal from "./modals/AddAvrModal";
import {
  createContragentAvr,
  downloadSignedAvr,
  listContragentAvr,
  signContragentAvr,
} from "./services";

const downloadBlob = (blob, fileName) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
};

const useStyles = makeStyles({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 25,
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    padding: 24,
  },
});

const ContragentAvr = ({ remont_id: remontIdProp }) => {
  const classes = useStyles();
  const { addDefaultSnack, addCloseSnack } = useCustomSnackbar();
  const { remont_id: remontIdParam } = useParams();
  const remont_id = remontIdProp ?? Number(remontIdParam);

  const [rows, setRows] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    if (!remont_id) {
      setRows([]);
      return;
    }

    setIsFetching(true);
    const data = await listContragentAvr(remont_id);
    setIsFetching(false);
    setRows(data || []);
  }, [remont_id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async (body) => {
    const isCreated = await createContragentAvr(body);
    if (!isCreated) {
      return false;
    }

    await loadData();
    addDefaultSnack("Успешно");
    return true;
  };

  const handleSign = async (contragentAvrId) => {
    const signUrl = await signContragentAvr(contragentAvrId, {
      return_url: window.location.href,
    });

    if (!signUrl) {
      return;
    }

    window.open(signUrl, "_blank", "noopener,noreferrer");
  };

  const handleOpenSigned = async (contragentAvrId) => {
    try {
      const res = await downloadSignedAvr(contragentAvrId);
      const blob = res.data;
      const contentType = res.headers["content-type"] || "";

      if (contentType.includes("application/json")) {
        const text = await blob.text();
        const json = JSON.parse(text);
        addCloseSnack(json?.error || "Не удалось скачать документ");
        return;
      }

      downloadBlob(blob, `avr_${contragentAvrId}.pdf`);
    } catch (err) {
      let message = err.message;
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const json = JSON.parse(text);
          if (json.error) {
            message = json.error;
          }
        } catch (e) {
          // ignore parse errors
        }
      }
      addCloseSnack(message || "Не удалось скачать документ");
    }
  };

  return (
    <Box className={classes.wrapper}>
      {isFetching && !rows.length ? (
        <Box className={classes.loader}>
          <LoaderData small />
        </Box>
      ) : (
        <AvrTable
          rows={rows}
          remontId={remont_id}
          onAddAvr={() => setIsAddModalOpen(true)}
          onSign={handleSign}
          onOpenSigned={handleOpenSigned}
        />
      )}

      <AddAvrModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        remontId={remont_id}
        onSubmit={handleCreate}
      />
    </Box>
  );
};

ContragentAvr.propTypes = {
  remont_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ContragentAvr;
